import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { FileText, Settings, Trash2, Link as LinkIcon , Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FlowNodeProps, ContentBlock } from '@/types/flow';
import BlocksPreview from './BlocksPreview';
import { ButtonStat } from '../../stats/FunnelStatsContext';

const MessageTemplateNode = ({ data, selected }: FlowNodeProps) => {
    const config = data.config || {};
    const title = config.title || '';
    const buttons = data.buttons || [];

    const allButtonsAreUrl = buttons.length > 0 && buttons.every((btn) => btn.button_type === 'url');

    const truncateMessage = (text: string, maxLength: number = 80) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-[#7C4DFF] shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
            style={{ borderTop: selected ? undefined : '1px solid #e5e7eb', borderRight: selected ? undefined : '1px solid #e5e7eb', borderBottom: selected ? undefined : '1px solid #e5e7eb' }}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-purple-50 rounded-lg flex items-center justify-center">
                        <FileText className="w-3.5 h-3.5 text-[#7C4DFF]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">{data.node_name || 'Template'}</span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Template</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-2.5">
                {title ? (
                    <div className="bg-[#f0f4f8] rounded-lg rounded-tl-sm px-3 py-2 text-[12px] text-gray-700 leading-relaxed mb-2">
                        {truncateMessage(title)}
                    </div>
                ) : (
                    <div className="text-center py-2 mb-2">
                        <p className="text-xs text-gray-400">Mensagem não configurada</p>
                    </div>
                )}

                {buttons.length > 0 ? (
                    <div className="space-y-1.5">
                        {buttons.slice(0, 3).map((button, idx) => (
                            <div
                                key={button.id || idx}
                                className="relative text-[11px] bg-purple-50 border border-purple-100 rounded-md px-3 py-2 text-[#7C4DFF] text-center font-medium flex items-center justify-center gap-1.5 min-h-[36px] overflow-visible"
                            >
                                {button.button_type === 'url' && (
                                    <LinkIcon className="w-3 h-3 text-[#7C4DFF] flex-shrink-0" />
                                )}
                                <span className="truncate flex-1">{button.button_text || `Botão ${idx + 1}`}</span>
                                <ButtonStat nodeId={data.id} buttonId={button.id} />

                                {button.button_type === 'postback' && (
                                    <Handle
                                        type="source"
                                        position={Position.Right}
                                        id={`button-${button.id}`}
                                        style={{
                                            position: 'absolute',
                                            right: '-10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: '14px',
                                            height: '14px',
                                            background: '#2196F3',
                                            border: '2px solid white',
                                            borderRadius: '50%',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            cursor: 'crosshair',
                                            zIndex: 10,
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                        {buttons.length > 3 && (
                            <span className="text-[10px] text-gray-400 block text-center">+{buttons.length - 3} botões</span>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-1">
                        <p className="text-[11px] text-gray-400">Nenhum botão</p>
                    </div>
                )}
            </div>

            <BlocksPreview blocks={(config.blocks || []) as ContentBlock[]} />

            {/* Actions */}
            <div className="border-t border-gray-100 px-3 py-1.5 flex gap-1">
                <Button size="sm" variant="ghost" className="flex-1 text-gray-500 hover:text-purple-600 hover:bg-purple-50/50 h-7 text-[11px]" onClick={() => data.onEdit?.(data.id)}>
                    <Settings className="w-3 h-3 mr-1" />
                    Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 h-7 px-2" title="Duplicar" onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}>
                    <Copy className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2" onClick={() => data.onDelete?.(data.id)}>
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>

            <Handle type="target" position={Position.Left} id="target" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />

            {(allButtonsAreUrl || buttons.length === 0) && (
                <Handle type="source" position={Position.Right} id="source" className="!w-3.5 !h-3.5 !bg-[#2196F3] !border-2 !border-white" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            )}
        </div>
    );
};

export default memo(MessageTemplateNode);
